import React from 'react';

import { Box, Typography } from '@mui/material';
import { TableCellDate, TooltipedIcon } from '@europrocurement/flexy-components';
import { ColumnDatatable } from '@europrocurement/flexy-datatable';
import { FactureOpenSearch } from '@europrocurement/l2d-domain';
import { demoIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import useFactureColumnsRenderFunctions from './useFactureColumnsRenderFunctions';

const useFactureColumns = () => {
    const { referenceDossierColumnRender, statusColumnRender } = useFactureColumnsRenderFunctions();

    const numeroFactureColumn: ColumnDatatable = {
        label: 'N° Facture',
        render: ({ factureNumero, demo }: FactureOpenSearch) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {!!demo && (
                    <TooltipedIcon
                        icon={demoIcon}
                        tooltip="Démo"
                        size="xs"
                        color="error"
                    />
                )}
                <Typography
                    variant="l2dtableCell"
                    sx={{ color: 'text.gris70' }}
                >
                    {factureNumero}
                </Typography>
            </Box>
        ),
        field: 'factureNumero',
        isDisplayed: true,
        sortable: true,
    };

    const referencesDossierColumn: ColumnDatatable = {
        label: 'Références dossiers',
        render: ({ dossiers, reference }: FactureOpenSearch) =>
            referenceDossierColumnRender(dossiers, reference),
        isDisplayed: true,
        sortable: true,
        field: 'reference',
    };

    const societeFactureeColumn: ColumnDatatable = {
        label: 'Société facturée',
        render: ({ factureClientRaisonSociale }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {' '}
                {factureClientRaisonSociale as string}
            </Typography>
        ),
        isDisplayed: true,
        sortable: true,
        field: 'societeFacturee',
    };

    const typeFormaliteColumn: ColumnDatatable = {
        label: 'Type de formalité',
        render: ({ typeFormalite }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {typeFormalite}
            </Typography>
        ),
        isDisplayed: true,
        field: 'typeFormalite',
        sortable: true,
    };

    const provisionColumn: ColumnDatatable = {
        label: 'Provision',
        render: ({ montantPaiementRecu }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {montantPaiementRecu && `${montantPaiementRecu} €`}
            </Typography>
        ),
        isDisplayed: true,
        sortable: true,
        field: 'montantPaiementRecu',
    };
    const montantTTCColumn: ColumnDatatable = {
        label: 'Montant TTC',
        render: ({ totalTtc }: FactureOpenSearch) => (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {totalTtc && `${totalTtc} €`}
            </Typography>
        ),
        isDisplayed: true,
        sortable: true,
        field: 'totalTtc',
    };

    const dateColumn: ColumnDatatable = {
        label: 'Date',
        render: ({ dateFacture }: FactureOpenSearch) =>
            dateFacture && (
                <TableCellDate
                    date={dateFacture}
                    displayCalendar
                />
            ),
        isDisplayed: true,
        sortable: true,
        field: 'dateFacture',
    };

    const dateLimiteColumn: ColumnDatatable = {
        label: 'Échéance',
        render: ({ dateEcheanceFacture }: FactureOpenSearch) =>
            dateEcheanceFacture && (
                <TableCellDate
                    date={dateEcheanceFacture}
                    displayCalendar
                />
            ),
        isDisplayed: true,
        sortable: true,
        field: 'dateEcheanceFacture',
    };

    const statutColumn: ColumnDatatable = {
        label: 'Statut',
        render: ({ status }: FactureOpenSearch) => statusColumnRender(status),
        isDisplayed: true,
        sortable: true,
    };

    const prescripteurColumn: ColumnDatatable = {
        label: 'Prescripteur',
        render: ({ prescripteurLibelle, demo }: FactureOpenSearch) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {!!demo && (
                    <TooltipedIcon
                        icon={demoIcon}
                        tooltip="Démo"
                        size="xs"
                        color="error"
                    />
                )}
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {prescripteurLibelle}
                </Typography>
            </Box>
        ),
        isDisplayed: true,
        sortable: false,
    };

    const countDossiersColumn: ColumnDatatable = {
        label: 'Dossiers',
        render: ({ dossiers }: FactureOpenSearch) => (
            <>
                <FaOptionIcon
                    icon={['fasl', 'folder']}
                    sx={{ fontSize: '1rem' }}
                />
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {dossiers && `${dossiers.length} `}
                </Typography>
            </>
        ),
        isDisplayed: true,
        sortable: false,
    };

    const columnsFront: ColumnDatatable[] = [
        numeroFactureColumn,
        referencesDossierColumn,
        societeFactureeColumn,
        typeFormaliteColumn,
        provisionColumn,
        montantTTCColumn,
        dateColumn,
        dateLimiteColumn,
        statutColumn,
    ];

    const formalitiesInvoicesColumnsBack: ColumnDatatable[] = [
        numeroFactureColumn,
        referencesDossierColumn,
        prescripteurColumn,
        societeFactureeColumn,
        typeFormaliteColumn,
        provisionColumn,
        montantTTCColumn,
        dateColumn,
        dateLimiteColumn,
        countDossiersColumn,
        statutColumn,
    ];

    return { columnsFront, formalitiesInvoicesColumnsBack };
};

export default useFactureColumns;
